import type { ComponentType, SVGProps } from 'react'
import {
  AlertTriangle,
  Bell01,
  CheckCircle,
  Clock,
  FileCheck02,
  FlipBackward,
  MessageTextSquare01,
  RefreshCcw01,
  Send01,
  Stars01,
  XCircle,
} from '@untitled-ui/icons-react'
import type { BadgeTone } from '@/theme/badge-tokens'
import type { InAppNotification } from '../api/types'
import { mapMarevaRemoteState } from './mapMarevaRemoteState'
import { mapTrackingStatusNotice } from './mapTrackingStatusNotice'

export type NotificationVisual = {
  Icon: ComponentType<SVGProps<SVGSVGElement>>
  /** Ton des pastilles du thème : fond de la tuile et couleur de l'icône. */
  tone: BadgeTone
}

const visual = (
  Icon: NotificationVisual['Icon'],
  tone: BadgeTone
): NotificationVisual => ({ Icon, tone })

const getStatusChangedVisual = (
  payload: Record<string, unknown>
): NotificationVisual => {
  const { labelKey, to } = payload
  if (labelKey === 'remote_state' && typeof to === 'string') {
    switch (mapMarevaRemoteState(to)) {
      case 'accepted':
        return visual(CheckCircle, 'green')
      case 'processing':
        return visual(Clock, 'warn')
      case 'rejected':
        return visual(XCircle, 'danger')
      default:
        break
    }
  }
  if (labelKey === 'tracking_status') {
    // Tons de l'écran Dossiers : « À traiter » et « Envoi échoué » en orange, « Erreur » en rouge.
    switch (mapTrackingStatusNotice(typeof to === 'string' ? to : null)) {
      case 'toTreat':
        return visual(FileCheck02, 'warn')
      case 'exportFailed':
        return visual(Send01, 'warn')
      case 'error':
        return visual(AlertTriangle, 'danger')
      default:
        break
    }
  }
  return visual(RefreshCcw01, 'green')
}

export const getNotificationVisual = (
  notification: Pick<InAppNotification, 'type' | 'payload'>
): NotificationVisual => {
  switch (notification.type) {
    case 'declaration_status_changed':
      return getStatusChangedVisual(notification.payload)
    case 'declaration_comment_added':
      return visual(MessageTextSquare01, 'violet')
    case 'declaration_sent_by_collaborator':
      return visual(Send01, 'blue')
    case 'declaration_returned_for_correction':
      return visual(FlipBackward, 'danger')
    case 'app_update':
      return visual(Stars01, 'violet')
    default:
      return visual(Bell01, 'neutral')
  }
}
